import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, Store } from 'lucide-react';
import { switchStore as switchStoreApi } from '@/lib/api/auth.js';
import { listStores } from '@/lib/api/stores.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError } from '@/lib/toast.js';

export function StoreSwitcher() {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const switchStore = useAuthStore((s) => s.switchStore);

  // All authenticated roles may need the list — filter client-side by storeIds.
  const storesQuery = useQuery({
    queryKey: ['stores-list'],
    queryFn: listStores,
    enabled: Boolean(user),
  });

  const allStores = storesQuery.data ?? [];
  const assignedStoreIds = user?.storeIds;
  const isElevated = user?.role === 'tenant_admin' || user?.role === 'platform_admin';

  const availableStores = useMemo(() => {
    const active = allStores.filter((s) => s.active);
    // null storeIds = access to all tenant stores (same as API switch-store)
    if (isElevated || assignedStoreIds == null) return active;
    if (assignedStoreIds.length === 0) return active;
    return active.filter((s) => assignedStoreIds.includes(s.id));
  }, [allStores, assignedStoreIds, isElevated]);

  const showSwitcher = availableStores.length > 1;

  const currentStore =
    availableStores.find((s) => s.id === user?.storeId) ??
    (user?.storeId
      ? {
          id: user.storeId,
          name: storesQuery.isLoading ? 'Loading…' : 'Current Store',
        }
      : null);

  const mutation = useMutation({
    mutationFn: (storeId: string) => switchStoreApi(storeId),
    onSuccess: (data) => {
      switchStore(data.user, data.token);
      window.location.reload();
    },
    onError: (error) => {
      toastError(parseApiError(error).message);
    },
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Single-store tenants: hide the switcher entirely.
  if (!showSwitcher) {
    return null;
  }

  if (!currentStore) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Store className="h-4 w-4 text-primary" />
        <span className="max-w-[140px] truncate">{currentStore.name}</span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>

      {open ? (
        <div
          className="absolute right-0 z-40 mt-1 min-w-[200px] rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800"
          role="listbox"
        >
          {availableStores.map((store) => (
            <button
              key={store.id}
              type="button"
              role="option"
              aria-selected={store.id === user?.storeId}
              disabled={mutation.isPending || store.id === user?.storeId}
              className={[
                'block w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700',
                store.id === user?.storeId
                  ? 'font-bold text-primary'
                  : 'text-slate-700 dark:text-slate-200',
              ].join(' ')}
              onClick={() => {
                setOpen(false);
                if (store.id !== user?.storeId) mutation.mutate(store.id);
              }}
            >
              {store.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
